import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCommentsByPostId,
  selectCommentsLoading,
  selectCommentsError,
  selectCommentsCountByPostId,
} from "../redux/selectors/commentsSelectors";
import {
  fetchCommentsByPost,
  createComment,
  updateComment as updateCommentThunk,
  deleteComment as deleteCommentThunk,
} from "../redux/thunks/commentsThunks";

const toKey = (v) => (v == null ? "" : String(v).trim());

export default function useComments(postId) {
  const dispatch = useDispatch();

  const postKey = useMemo(() => {
    const k = toKey(postId);
    return k || null;
  }, [postId]);

  const comments = useSelector((state) =>
    postKey ? selectCommentsByPostId(state, postKey) : []
  );
  const isLoading     = useSelector(selectCommentsLoading);
  const error         = useSelector(selectCommentsError);
  const commentsCount = useSelector((state) =>
    postKey ? selectCommentsCountByPostId(state, postKey) : 0
  );

  const fetchComments = useCallback(() => {
    if (!postKey) return Promise.resolve(undefined);
    return dispatch(fetchCommentsByPost(postKey));
  }, [dispatch, postKey]);

  const addComment = useCallback(
    async (dto) => {
      const resolvedPostId = toKey(dto?.postId);
      if (!resolvedPostId) {
        console.warn("⚠️ addComment: postId manquant dans dto", dto);
        return Promise.resolve(undefined);
      }

      const payload = { ...dto, postId: resolvedPostId };

      const res = await dispatch(createComment(payload));
      if (!res?.error) dispatch(fetchCommentsByPost(resolvedPostId));
      return res;
    },
    [dispatch]
  );

  // ✅ FIX: accepte un objet DTO (compatible avec PostCard) OU (commentId, content) legacy
  const updateComment = useCallback(
    async (dtoOrCommentId, legacyContent) => {
      // Détecte si appelé avec un objet { commentId, content, postId, ... }
      // ou avec l'ancienne signature (commentId, content)
      const isObjectCall =
        dtoOrCommentId !== null &&
        typeof dtoOrCommentId === "object" &&
        !Array.isArray(dtoOrCommentId);

      const cid = isObjectCall
        ? toKey(dtoOrCommentId?.commentId)
        : toKey(dtoOrCommentId);

      const txt = isObjectCall
        ? String(dtoOrCommentId?.content ?? "").trim()
        : String(legacyContent ?? "").trim();

      const pid = isObjectCall
        ? toKey(dtoOrCommentId?.postId ?? postKey)
        : toKey(postKey);

      // Acteur (optionnel, utile si le backend vérifie l'autorisation)
      const doctorId = isObjectCall ? dtoOrCommentId?.doctorId ?? undefined : undefined;
      const userId   = isObjectCall ? dtoOrCommentId?.userId   ?? undefined : undefined;

      if (!pid || !cid || !txt) {
        console.warn("⚠️ updateComment: paramètres invalides", { cid, pid, txt });
        return Promise.resolve(undefined);
      }

      const res = await dispatch(
        updateCommentThunk({ commentId: cid, postId: pid, content: txt, doctorId, userId })
      );
      if (!res?.error) dispatch(fetchCommentsByPost(pid));
      return res;
    },
    [dispatch, postKey]
  );

  const deleteComment = useCallback(
    async (dtoOrCommentId) => {
      // Accepte { commentId, postId } ou juste un commentId string/number
      const isObjectCall =
        dtoOrCommentId !== null &&
        typeof dtoOrCommentId === "object" &&
        !Array.isArray(dtoOrCommentId);

      const cid = isObjectCall
        ? toKey(dtoOrCommentId?.commentId)
        : toKey(dtoOrCommentId);

      const pid = isObjectCall
        ? toKey(dtoOrCommentId?.postId ?? postKey)
        : toKey(postKey);

      if (!pid || !cid) {
        console.warn("⚠️ deleteComment: paramètres invalides", { cid, pid });
        return Promise.resolve(undefined);
      }

      const res = await dispatch(
        deleteCommentThunk({ commentId: cid, postId: pid })
      );
      if (!res?.error) dispatch(fetchCommentsByPost(pid));
      return res;
    },
    [dispatch, postKey]
  );

  return {
    comments,
    commentsCount,
    isLoading,
    error,
    fetchComments,
    addComment,
    updateComment,
    deleteComment,
    editComment:   updateComment,
    removeComment: deleteComment,
  };
}